/**
 * Header mega-menu behaviour.
 *
 * Per top-level item (`.tx-hdr__nav-item--has-mega`):
 *   - mouseenter  → open its panel (close any other open panel)
 *   - mouseleave  → schedule close after CLOSE_DELAY ms
 *   - focusin     → open
 *   - Esc         → close everything
 *   - click away  → close everything
 *
 * Inside an open panel, hovering or focusing a `.tx-mega__cat` switches
 * the visible col-2 / col-3 pane by flipping `data-active` attributes.
 *
 * All content is pre-rendered server-side; this script is purely about
 * open/close state and which pane is currently shown.
 */

const SELECTOR_HOST  = '.tx-hdr__nav-item--has-mega';
const SELECTOR_PANEL = '.tx-mega';
const SELECTOR_CAT   = '.tx-mega__cat';
const SELECTOR_SUB   = '.tx-mega__subs';
const SELECTOR_FEAT  = '.tx-mega__feat';
const OPEN_CLASS     = 'is-open';
const CLOSE_DELAY    = 120;

/** Close every open dropdown and clear any pending close. */
const closeAllDropdowns = (hosts) => {
    hosts.forEach((host) => {
        host.classList.remove(OPEN_CLASS);
        const link = host.querySelector(':scope > a');
        if (link) link.setAttribute('aria-expanded', 'false');
        if (host._megaCloseTimer) {
            clearTimeout(host._megaCloseTimer);
            host._megaCloseTimer = null;
        }
    });
};

const openDropdown = (host, hosts) => {
    closeAllDropdowns(hosts);
    host.classList.add(OPEN_CLASS);
    const link = host.querySelector(':scope > a');
    if (link) link.setAttribute('aria-expanded', 'true');
    if (host._megaCloseTimer) {
        clearTimeout(host._megaCloseTimer);
        host._megaCloseTimer = null;
    }
};

const scheduleClose = (host) => {
    if (host._megaCloseTimer) clearTimeout(host._megaCloseTimer);
    host._megaCloseTimer = setTimeout(() => {
        host.classList.remove(OPEN_CLASS);
        const link = host.querySelector(':scope > a');
        if (link) link.setAttribute('aria-expanded', 'false');
        host._megaCloseTimer = null;
    }, CLOSE_DELAY);
};

/** Make the hovered col-1 item drive col-2 and col-3 visibility. */
const activatePane = (panel, target) => {
    panel.querySelectorAll(SELECTOR_CAT).forEach((cat) => {
        cat.setAttribute('data-active', cat.dataset.target === target ? 'true' : 'false');
    });
    panel.querySelectorAll(SELECTOR_SUB).forEach((sub) => {
        sub.setAttribute('data-active', sub.dataset.id === target ? 'true' : 'false');
    });
    panel.querySelectorAll(SELECTOR_FEAT).forEach((feat) => {
        feat.setAttribute('data-active', feat.dataset.id === target ? 'true' : 'false');
    });
};

const wirePanel = (panel) => {
    panel.querySelectorAll(SELECTOR_CAT).forEach((cat) => {
        const target = cat.dataset.target;
        if (!target) return;
        cat.addEventListener('mouseenter', () => activatePane(panel, target));
        cat.addEventListener('focusin',    () => activatePane(panel, target));
    });
};

const init = () => {
    const hosts = Array.from(document.querySelectorAll(SELECTOR_HOST));
    if (hosts.length === 0) return;

    hosts.forEach((host) => {
        const panel = host.querySelector(SELECTOR_PANEL);
        if (!panel) return;

        wirePanel(panel);

        host.addEventListener('mouseenter', () => openDropdown(host, hosts));
        host.addEventListener('mouseleave', () => scheduleClose(host));
        host.addEventListener('focusin',    () => openDropdown(host, hosts));
        host.addEventListener('focusout', (event) => {
            // If focus has left the host subtree entirely, schedule close.
            if (!host.contains(event.relatedTarget)) {
                scheduleClose(host);
            }
        });
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeAllDropdowns(hosts);
    });

    document.addEventListener('click', (event) => {
        const inside = hosts.some((host) => host.contains(event.target));
        if (!inside) closeAllDropdowns(hosts);
    });
};

document.addEventListener('DOMContentLoaded', init);
